<template>
  <template v-if="props.pending">
    <div class="col-span-1 md:col-span-2 xl:col-span-2 1xl:col-span-3 2xl:col-span-3 animate-pulse">
      <div class="bg-neutral-100 w-[210px] h-4.5 rounded-full mb-1" />
    </div>
    <div v-for="object in 24" :key="object" class="flex flex-col">
      <SkeletonSharedObjectCard design="searchresult" />
    </div>
  </template>
  <template v-if="!props.pending && (props?.data?.hits ?? 0) > 0">
    <div class="flex flex-col col-span-1 md:col-span-1 xl:col-span-1 1xl:col-span-2 text-left lg:gap-2 gap-2 items-start justify-start flex-wrap mb-2">
      <div>
        <UiHeadline v-if="props.pageTitle && props.pageTitle !== ''" design="h3" type="h1" class="inline-block mr-1">
          {{ props.pageTitle }}
        </UiHeadline>
        <UiHeadline v-if="props.pageTitle && props.pageTitle !== ''" design="h3" type="div" class="inline-block">
          ({{ props.data?.hits ?? 0 }})
        </UiHeadline>
        <UiHeadline v-if="props.showOnlyLastminuteObjects && !props.pageTitle" design="h3" type="h1" class="inline-block mr-1">
          {{
            t(
              `search_results_count_lastminute.${numerus(props.data?.hits ?? 0)}`,
              {
                hits: (props.data?.hits ?? 0).toLocaleString('de-DE'),
                preposition: links?.preposition,
                location_or_portal_name: links?.location ?? links?.portal_name ?? ''
              }
            )
          }}
          {{ portalName }}
        </UiHeadline>
        <UiHeadline v-if="!props.showOnlyLastminuteObjects && (!props.pageTitle || props.pageTitle === '')" design="h3" type="h1">
          {{
            t(
              `search_results_count.${numerus(props.data?.hits ?? 0)}`,
              { hits: (props.data?.hits ?? 0).toLocaleString('de-DE') }
            )
          }}
        </UiHeadline>
      </div>
      <div class="text-sm text-left">
        <button
          class="font-semibold underline hover:text-petrol-500 cursor-pointer mr-1"
          aria-haspopup="true"
          tabindex="0"
          role="button"
          @click="clickTriggerModal"
          @keydown.enter="clickTriggerModal"
        >
          <UiLanguagePlaceholder domain="SearchResults" name="more_information_sorting" />
        </button>
        <UiLanguagePlaceholder domain="SearchResults" name="more_information_sorting_2" />
      </div>
      <UiModal :modal-id="modalId" :open="showSortingModals" width="md:w-120" @hidden="showSortingModals = false">
        <template #head>
          {{ t('sorting_modal_headline') }}
        </template>
        <div class="text-left">
          <UiLanguagePlaceholder domain="SearchResults" name="sorting_modal_text" />
        </div>
      </UiModal>
    </div>
    <div class="hidden lg:block col-span-1 md:col-span-1 xl:col-span-1 1xl:col-span-1">
      <div class="flex h-full items-center justify-end ">
        <SearchFilterSorting :params="searchParamsStore" design="pill" width="w-auto" @update="update($event)" />
      </div>
    </div>
    <div
      :class="['col-span-1 md:col-span-2 xl:col-span-2 1xl:col-span-3 2xl:col-span-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 1xl:grid-cols-3 2xl:grid-cols-3 gap-4', {'mt-[50px]': hasHighlightedObject}]"
    >
      <div v-for="(objekt, index) in props.data?.normal_results?.plain" :id="'objekt-' + objekt.nr" :key="objekt.nr" class="flex flex-col relative">
        <SharedObjectCard
          design="searchresult"
          :masked-link="true"
          :item="objekt"
          :lazy-loading="index!==0"
          :highlighted="props.highlightedObjectNr === objekt.nr"
          :highlighted-icon-url="props.highlightedObjectNr === objekt.nr ? props.highlightedObjectIconUrl : ''"
          :serp-highlighted="objekt.nr == searchParamsStore.serp_highlight"
          @mouseenter="setHighlightObject(objekt.nr)"
          @mouseleave="removeHighlightObject()"
        />
      </div>
      <template v-for="(strategy, index1) in props.data?.alternative_results?.strategies" :key="strategy.name">
        <div
          v-if="strategy?.plain?.length > 0"
          class="col-span-1 md:col-span-2 xl:col-span-2 1xl:col-span-3 2xl:col-span-3 leading-4 xl:leading-5 text-left text-base xl:text-xl text-petrol-500 pt-3 pb-3 flex items-center gap-x-3"
        >
          <div class="bg-petrol-500 flex-none flex items-center p-1">
            <UiIcon
              v-if="['flexi_arrival', 'flexi_nights'].includes(strategy.name)"
              src="/svg/icons/time.svg"
              width="26"
              height="26"
              alt="Alternativer Zeitraum"
              class="fill-white inline-block"
            />
            <UiIcon
              v-else-if="strategy.name === 'radius'"
              src="/svg/icons/location.svg"
              width="26"
              height="26"
              alt="Alternativer Zeitraum"
              class="fill-white inline-block"
            />
          </div>
          <UiLanguagePlaceholder
            v-if="['flexi_arrival', 'flexi_nights'].includes(strategy.name)"
            domain="SearchResults"
            name="alternative_results_title_flexi"
          />
          <UiLanguagePlaceholder
            v-else-if="strategy.name === 'radius'"
            domain="SearchResults"
            name="alternative_results_title_radius"
            :variables="{ ort: props.data?.location?.ort ?? links?.location ?? '' }"
          />
        </div>
        <div v-for="(objekt, index2) in strategy?.plain" :id="'objekt-' + objekt.nr" :key="objekt.nr" class="flex flex-col">
          <SharedObjectCard
            design="searchresult"
            :masked-link="true"
            :alternative-object="true"
            :item="objekt"
            :lazy-loading="(props.data?.normal_results?.hits ?? 0)>0 || (index1!==0 && index2!==0)"
            :highlighted="props.highlightedObjectNr === objekt.nr"
            :highlighted-icon-url="props.highlightedObjectNr === objekt.nr ? props.highlightedObjectIconUrl : ''"
            @mouseenter="setHighlightObject(objekt.nr)"
            @mouseleave="removeHighlightObject()"
          />
        </div>
      </template>
    </div>
  </template>
  <template v-if="(props?.data?.hits ?? 0) === 0">
    <div class="col-span-1 md:col-span-2 xl:col-span-2 1xl:col-span-3 2xl:col-span-3">
      <UiAlert design="error" class="text-left">
        <UiHeadline design="h4" type="h3">
          {{ t('no_results_title') }}
        </UiHeadline>
        <p>{{ t('no_results_body') }}</p>
      </UiAlert>
    </div>
  </template>
</template>

<script setup lang="ts">
import { useSearchResultsMapStore } from '~~/stores/searchResultsMap'
import { useSearchParamsStore } from '~~/stores/searchParams'
import { useGuestsStore } from '~~/stores/guests'
import { useTravelDatesStore } from '~~/stores/travelDates'

const props = defineProps({
  data: {
    type: Object,
    default: null
  },
  pending: {
    type: Boolean,
    default: true
  },
  pageTitle: {
    type: String,
    default: null
  },
  highlightedObjectNr: {
    type: Number,
    default: -1
  },
  highlightedObjectIconUrl: {
    type: String,
    default: ''
  },
  showOnlyLastminuteObjects: {
    type: Boolean,
    default: false
  }
})

const { translate: t, numerus } = useTranslations('SearchResults')
const { links } = usePortalData()
const showSortingModals = ref(false)
const modalId = useComponentId('modal-search-results-sorting')
const mapStore = useSearchResultsMapStore()
const searchParamsStore = useSearchParamsStore()

const update = (event) => {
  const updatedParams = {}
  Object.entries(event).forEach(([key, value]) => {
    updatedParams[key] = value
  })
  searchParamsStore.$patch(updatedParams)
}

function setHighlightObject (index: number) {
  mapStore.hoveredObject = index
}
function removeHighlightObject () {
  mapStore.hoveredObject = null
}
const hasHighlightedObject = computed(() => {
  return props.data?.normal_results?.plain.some(objekt => objekt.nr === searchParamsStore.serp_highlight)
})
function productImpressionTracking () {
  const { $gtmModules } = useNuxtApp()
  const guests = useGuestsStore()
  const travelDates = useTravelDatesStore()
  const combinedResults = useSearchResultsHelper().getCombinedResults(
    props.data?.normal_results?.plain,
    props.data?.alternative_results?.strategies
  )
  $gtmModules.productsModule.sendProductImpressionEvent(combinedResults, guests.personen, travelDates.arrival, travelDates.departure)
}

if (import.meta.client) {
  productImpressionTracking()
}

watch(() => props.data, () => {
  productImpressionTracking()
})

const clickTriggerModal = () => {
  showSortingModals.value = true
}
</script>
